/*
  Mini cart v2 (sections/mini-cart.liquid).
  The drawer is re-rendered as a whole after every cart change, so every element here
  re-initialises itself in connectedCallback, and shelf open/closed state lives in a module-level map.
*/
(function () {
  const shelfState = {};
  const recsCache = {};
  let recsLast = '';
  const scriptEl = document.currentScript;
  let splidePromise;

  /* Splide ships with the theme but only loads on pages (theme.liquid, lp-init), so fetch it on demand. */
  function loadSplide() {
    if (window.Splide) return Promise.resolve(window.Splide);
    if (splidePromise) return splidePromise;
    const data = (scriptEl && scriptEl.dataset) || {};
    if (data.splideCss && !document.querySelector('link[href*="splide.min.css"]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = data.splideCss;
      document.head.appendChild(link);
    }
    splidePromise = new Promise((resolve, reject) => {
      if (!data.splideJs) return reject(new Error('mini-cart-v2: splide.min.js url missing'));
      const script = document.createElement('script');
      script.src = data.splideJs;
      script.onload = () => (window.Splide ? resolve(window.Splide) : reject(new Error('Splide failed to load')));
      script.onerror = reject;
      document.head.appendChild(script);
    });
    return splidePromise;
  }

  function getMiniCart() {
    return document.querySelector('mini-cart');
  }

  function renderCartSections(parsedState) {
    const miniCart = getMiniCart();
    const cartItems = document.querySelector('#mini-cart cart-items');
    if (cartItems && typeof cartItems.getSectionsToRender === 'function') {
      cartItems.getSectionsToRender().forEach((section) => {
        const element = document.getElementById(section.id);
        const html = parsedState.sections && parsedState.sections[section.section];
        if (!element || !html) return;
        const target = element.querySelector(section.selector) || element;
        const inner = cartItems.getSectionInnerHTML(html, section.selector);
        if (inner !== undefined) target.innerHTML = inner;
      });
    } else if (miniCart) {
      miniCart.renderContents(parsedState);
    }
    publish(PUB_SUB_EVENTS.cartUpdate, { source: 'mini-cart-v2' });
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: parsedState } }));
  }

  function sectionIds() {
    const cartItems = document.querySelector('#mini-cart cart-items');
    if (cartItems && typeof cartItems.getSectionsToRender === 'function') {
      return cartItems.getSectionsToRender().map((section) => section.section).filter(Boolean);
    }
    const miniCart = getMiniCart();
    return miniCart ? miniCart.getSectionsToRender().map((section) => section.section) : [];
  }

  /* Subscription / one-time switcher on a cart line. Changes the line's selling plan in place. */
  class Mcv2Plan extends HTMLElement {
    connectedCallback() {
      this.select = this.querySelector('select');
      if (!this.select || this.bound) return;
      this.bound = true;
      this.initial = this.select.value;
      this.addEventListener('change', (event) => {
        // Keep the event away from <cart-items>, whose change handler treats it as a quantity input.
        event.stopPropagation();
        this.onChange();
      });
    }

    onChange() {
      const value = this.select.value;
      if (value === this.initial) return;

      const line = this.closest('.mcv2-line');
      const overlay = line && line.querySelector('.loading-overlay');
      overlay && overlay.classList.remove('hidden');
      this.select.disabled = true;
      const mainItems = document.getElementById('main-cart-items');
      mainItems && mainItems.classList.add('cart__items--disabled');

      const body = JSON.stringify({
        id: this.dataset.key,
        quantity: parseInt(this.dataset.quantity, 10) || 1,
        selling_plan: value ? parseInt(value, 10) : null,
        sections: sectionIds(),
        sections_url: window.location.pathname
      });

      fetch(`${theme.routes.cart_change_url}`, { ...fetchConfig(), body })
        .then((response) =>
          response.json().then((parsedState) => {
            // Errors come back as e.g. 422 {"errors":"Cart Error"} with no status field and no
            // sections, so judge by the HTTP status; otherwise the line stays locked forever.
            if (!response.ok || !parsedState.sections) {
              throw new Error(parsedState.description || parsedState.message || parsedState.errors || response.status);
            }
            return parsedState;
          })
        )
        .then((parsedState) => renderCartSections(parsedState))
        .catch((error) => {
          console.error(error);
          this.select.value = this.initial;
          this.select.disabled = false;
          overlay && overlay.classList.add('hidden');
          const lineError = line && line.querySelector('.cart-item__error-text');
          if (lineError) lineError.textContent = (theme.cartStrings && theme.cartStrings.error) || 'Something went wrong. Please try again.';
        })
        .finally(() => {
          mainItems && mainItems.classList.remove('cart__items--disabled');
        });
    }
  }
  customElements.define('mcv2-plan', Mcv2Plan);

  /* Auto-open of the sampler shelf (theme setting cart_sample_autoopen → data-autoopen, in seconds).
     Counts from the moment the drawer opens and fires once per visit (sessionStorage). The drawer's
     contents may still be loading when it opens, so the shelf calls check() again when it connects. */
  const autoOpen = {
    key: 'mcv2-sample-autoopen',
    openedAt: 0,
    timer: null,
    isDone() {
      try {
        return window.sessionStorage.getItem(this.key) === 'done';
      } catch (error) {
        return !!this.doneInMemory;
      }
    },
    done() {
      this.doneInMemory = true;
      clearTimeout(this.timer);
      try {
        window.sessionStorage.setItem(this.key, 'done');
      } catch (error) {}
    },
    start() {
      this.openedAt = Date.now();
      this.check();
    },
    stop() {
      this.openedAt = 0;
      clearTimeout(this.timer);
    },
    check() {
      clearTimeout(this.timer);
      if (!this.openedAt || this.isDone()) return;
      const shelf = document.querySelector('#mini-cart mcv2-shelf[data-autoopen]');
      if (!shelf) return;
      const remaining = parseFloat(shelf.dataset.autoopen) * 1000 - (Date.now() - this.openedAt);
      if (remaining > 0) {
        this.timer = setTimeout(() => this.check(), remaining);
        return;
      }
      if (!shelf.classList.contains('is-open')) shelf.setOpen(true);
      this.done();
    }
  };

  // The drawer is a <details>; its toggle event doesn't bubble, so listen in the capture phase.
  document.addEventListener('toggle', (event) => {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement) || !details.querySelector('#mini-cart')) return;
    if (details.open) autoOpen.start();
    else {
      autoOpen.stop();
      videoPeek.hide();
    }
  }, true);

  /* Collapsible shelf above the checkout footer ("Testing to Sample", "You may also like").
     Open/close is animated in CSS (grid-template-rows); the recommendations slider is Splide. */
  class Mcv2Shelf extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.bar = this.querySelector('.mcv2-shelf__bar');
      this.panel = this.querySelector('.mcv2-shelf__panel');
      this.bar.addEventListener('click', () => this.setOpen(!this.classList.contains('is-open')));
      this.panel.addEventListener('transitionend', (event) => {
        if (event.target === this.panel && this.classList.contains('is-open')) this.classList.add('is-settled');
      });
      this.setOpen(!!shelfState[this.dataset.shelf], true);

      this.closeButton = this.querySelector('.mcv2-shelf__close');
      if (this.closeButton) this.closeButton.addEventListener('click', () => this.setOpen(false));

      this.addEventListener('click', this.onAddClick.bind(this));
      if (this.dataset.autoopen) autoOpen.check();

      if (this.dataset.url) this.loadRecommendations();
      else this.initSlider();
    }

    disconnectedCallback() {
      if (this.slider) {
        this.slider.destroy(true);
        this.slider = null;
      }
    }

    setOpen(open, silent) {
      this.classList.toggle('is-open', open);
      this.bar.setAttribute('aria-expanded', open);
      this.panel.inert = !open;
      if (this.closeButton) this.closeButton.tabIndex = open ? 0 : -1;
      // Restored without animation (after a cart re-render), so it is settled straight away.
      const instant = parseFloat(getComputedStyle(this.panel).transitionDuration) === 0;
      this.classList.toggle('is-settled', open && (!!silent || instant));
      if (silent) return;
      // Both shelves may be open at once (Figma spec 17438:48, state D).
      shelfState[this.dataset.shelf] = open;
      // Any close by the shopper (X, bar, click elsewhere) ends auto-opening for this visit.
      if (!open && this.dataset.autoopen) autoOpen.done();
    }

    /* The drawer is re-rendered after every cart change, and this shelf with it. The last result is
       cached so a re-render shows it at once (no rise animation); a fresh fetch then updates it quietly.
       Only the very first reveal animates, by dropping .is-pending (see component-mini-cart-v2.css). */
    loadRecommendations() {
      const url = this.dataset.url;
      const cached = recsCache[url] || recsLast;
      if (cached) {
        this.fillRecs(cached);
        this.classList.remove('is-pending');
      }

      fetch(url)
        .then((response) => response.text())
        .then((text) => {
          const html = new DOMParser().parseFromString(text, 'text/html');
          const source = html.querySelector('[data-mcv2-recs]');
          const inner = source && source.querySelector('[data-mcv2-slide]') ? source.innerHTML : '';
          recsCache[url] = inner;
          if (!this.isConnected) return;
          if (!inner) {
            this.classList.add('is-pending');
            return;
          }
          recsLast = inner;
          if (inner !== this.recsHtml) this.fillRecs(inner);
          this.classList.remove('is-pending');
        })
        .catch((error) => console.error(error));
    }

    fillRecs(inner) {
      const list = this.querySelector('[data-mcv2-recs]');
      if (!list) return;
      if (this.slider) {
        this.slider.destroy(true);
        this.slider = null;
      }
      list.innerHTML = inner;
      this.recsHtml = inner;
      this.initSlider();
    }

    initSlider() {
      const root = this.querySelector('.splide');
      if (!root) return;
      const count = root.querySelectorAll('[data-mcv2-slide]').length;
      if (!count) return;

      loadSplide()
        .then((Splide) => {
          if (!this.isConnected || this.slider) return;
          const multiple = count > 1;
          this.slider = new Splide(root, {
            type: 'slide',
            perMove: 1,
            gap: '12px',
            // Desktop (spec 17438:48): 32px gutter, 412px card, next card peeks 24px (36 = 24 + 12 gap).
            padding: multiple ? { left: '32px', right: '36px' } : { left: '32px', right: '32px' },
            fixedWidth: '100%',
            breakpoints: {
              // Mobile frames: 12px inset, card = track - 38px.
              991: {
                padding: { left: '12px', right: '12px' },
                fixedWidth: multiple ? 'calc(100% - 38px)' : '100%'
              }
            },
            trimSpace: 'move',
            arrows: false,
            pagination: multiple,
            drag: multiple,
            noDrag: 'input, textarea, button',
            speed: 400
          }).mount();
        })
        .catch((error) => console.error(error));
    }

    onAddClick(event) {
      const button = event.target.closest('[data-mcv2-rec-add]');
      if (!button || button.disabled) return;
      event.preventDefault();

      const card = button.closest('[data-mcv2-slide]');
      const qtyInput = card && card.querySelector('[data-mcv2-rec-qty]');
      const quantity = Math.max(1, parseInt(qtyInput ? qtyInput.value : 1, 10) || 1);

      // Lock every add button in the shelf until the drawer re-renders, so quick clicks on
      // different samplers can't slip past the per-order limit computed in Liquid.
      const buttons = Array.from(this.querySelectorAll('[data-mcv2-rec-add]')).filter((b) => !b.disabled);
      buttons.forEach((b) => (b.disabled = true));
      button.classList.add('is-loading');

      const body = JSON.stringify({
        id: button.dataset.mcv2RecAdd,
        quantity,
        sections: sectionIds(),
        sections_url: window.location.pathname
      });

      fetch(`${theme.routes.cart_add_url}`, { ...fetchConfig('javascript'), body })
        .then((response) => response.json())
        .then((parsedState) => {
          if (parsedState.status === 422) {
            document.dispatchEvent(new CustomEvent('ajaxProduct:error', { detail: { errorMessage: parsedState.description } }));
            return;
          }
          renderCartSections(parsedState);
          document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: parsedState } }));
        })
        .catch((error) => console.error(error))
        .finally(() => {
          buttons.forEach((b) => (b.disabled = false));
          button.classList.remove('is-loading');
        });
    }
  }
  customElements.define('mcv2-shelf', Mcv2Shelf);

  /* Story bubble in the sampler shelf bar (theme setting cart_sample_video).
     Desktop hover: the bubble grows into a larger circle that loops the silent teaser (data-teaser).
     Click: the full video plays with sound in a popup. Neither <video> exists until it is needed, and both
     the hover circle and the popup live on <body>, outside the drawer, which re-renders on every cart change
     and scrolls its shelves (an enlarged bubble inside it would be clipped). */
  const videoSeenKey = 'mcv2-video-seen';
  const canHover = window.matchMedia('(hover: hover) and (pointer: fine) and (min-width: 992px)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function isVideoSeen() {
    try {
      return window.localStorage.getItem(videoSeenKey) === '1';
    } catch (error) {
      return false;
    }
  }

  function markVideoSeen() {
    try {
      window.localStorage.setItem(videoSeenKey, '1');
    } catch (error) {}
    document.querySelectorAll('mcv2-video').forEach((el) => el.classList.add('is-seen'));
  }

  /* The bubble points at the uploaded original; if that fails, switch once to Shopify's rendition. */
  function setVideoSource(video, src, fallback) {
    video.src = src;
    if (!fallback || fallback === src) return;
    video.addEventListener('error', () => {
      if (video.src.indexOf(fallback) !== -1) return;
      video.src = fallback;
      const play = video.play();
      if (play) play.catch(() => {});
    }, { once: true });
  }

  const videoPeek = {
    size: 168,
    el: null,
    video: null,
    src: '',
    build() {
      this.el = document.createElement('div');
      this.el.className = 'mcv2-video-peek';
      this.el.setAttribute('aria-hidden', 'true');
      this.el.innerHTML = '<div class="mcv2-video-peek__media"><img alt=""></div><span class="mcv2-video-peek__play"></span>';
      this.img = this.el.querySelector('img');
      this.media = this.el.querySelector('.mcv2-video-peek__media');
      // The enlarged circle covers the bubble, so it keeps itself open: it shrinks only when the cursor
      // leaves it (and doesn't go back onto the bubble), and a click on it opens the full video.
      this.el.addEventListener('mouseleave', (event) => {
        if (this.host && this.host.bubble.contains(event.relatedTarget)) return;
        this.hide();
      });
      this.el.addEventListener('click', (event) => {
        // Keep the drawer's outside-click handler (MenuDrawer.onBodyClick) from closing the cart.
        event.stopPropagation();
        const host = this.host;
        this.hide();
        if (host && host.isConnected) videoModal.open(host);
      });
      document.body.appendChild(this.el);
    },
    contains(node) {
      return !!(this.el && node && this.el.contains(node));
    },
    show(host) {
      if (!this.el) this.build();
      this.host = host;
      const bubble = host.bubble.getBoundingClientRect();
      const size = this.size;
      const cx = bubble.left + bubble.width / 2;
      const left = Math.min(Math.max(8, cx - size / 2), window.innerWidth - size - 8);
      const top = Math.max(8, bubble.bottom - size);
      // Grows out of the bubble: starts at the bubble's size, scaled around the bubble's centre.
      this.el.style.left = `${left}px`;
      this.el.style.top = `${top}px`;
      this.el.style.transformOrigin = `${cx - left}px ${bubble.top + bubble.height / 2 - top}px`;
      this.el.style.setProperty('--mcv2-peek-from', bubble.width / size);
      this.img.src = host.bubble.querySelector('img').currentSrc;

      const src = host.dataset.teaser;
      if (src !== this.src) {
        if (this.video) this.video.remove();
        this.video = document.createElement('video');
        this.video.muted = true;
        this.video.loop = true;
        this.video.playsInline = true;
        this.video.setAttribute('playsinline', '');
        this.video.preload = 'auto';
        setVideoSource(this.video, src, host.dataset.teaserFallback);
        this.video.addEventListener('playing', () => this.el.classList.add('is-playing'));
        this.media.appendChild(this.video);
        this.src = src;
      }
      this.el.classList.remove('is-playing');
      this.el.offsetWidth; // restart the grow transition from the bubble's size
      this.el.classList.add('is-active');
      const play = this.video.play();
      if (play) play.catch(() => {});
    },
    hide() {
      if (!this.el) return;
      this.host = null;
      this.el.classList.remove('is-active');
      if (this.video) this.video.pause();
    }
  };

  const videoModal = {
    el: null,
    video: null,
    src: '',
    host: null,
    build() {
      const dialog = document.createElement('dialog');
      dialog.className = 'mcv2-video-modal';
      dialog.innerHTML =
        '<div class="mcv2-video-modal__frame">' +
          '<button type="button" class="mcv2-video-modal__close" aria-label="Close">' +
            '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true" focusable="false"><path d="M1 1L13 13M13 1L1 13" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>' +
          '</button>' +
          '<div class="mcv2-video-modal__media"></div>' +
          '<button type="button" class="mcv2__cta mcv2-video-modal__cta" hidden></button>' +
        '</div>';
      this.el = dialog;
      this.frame = dialog.querySelector('.mcv2-video-modal__frame');
      this.media = dialog.querySelector('.mcv2-video-modal__media');
      this.cta = dialog.querySelector('.mcv2-video-modal__cta');
      dialog.querySelector('.mcv2-video-modal__close').addEventListener('click', () => this.close());
      // Click on the dimmed backdrop (the dialog itself, outside the frame) closes it.
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) this.close();
      });
      // The cart drawer closes on any body click outside itself (MenuDrawer.onBodyClick); this popup sits
      // on <body>, so keep its clicks from reaching that listener.
      dialog.addEventListener('click', (event) => event.stopPropagation());
      // Esc closes the dialog on keydown and focus returns to the bubble inside the drawer, so the keyup
      // would land there and close the drawer too (onKeyUpEscape in global.js). Swallow that one keyup.
      dialog.addEventListener('cancel', () => {
        const swallow = (event) => {
          if (event.key !== 'Escape') return;
          event.stopPropagation();
          document.removeEventListener('keyup', swallow, true);
        };
        document.addEventListener('keyup', swallow, true);
        setTimeout(() => document.removeEventListener('keyup', swallow, true), 1000);
      });
      dialog.addEventListener('close', () => {
        if (this.video) this.video.pause();
        document.documentElement.classList.remove('mcv2-video-open');
      });
      this.cta.addEventListener('click', () => this.addAll());
      document.body.appendChild(dialog);
    },
    open(host) {
      if (!this.el) this.build();
      const data = host.dataset;
      this.el.setAttribute('aria-label', data.label || '');
      this.frame.style.setProperty('--mcv2-video-ratio', parseFloat(data.ratio) || 0.5625);
      this.cta.hidden = !data.cta || !data.ctaIds;
      this.cta.textContent = data.cta || '';
      this.ctaIds = (data.ctaIds || '').split(',').filter(Boolean);

      if (data.src !== this.src) {
        if (this.video) this.video.remove();
        this.video = document.createElement('video');
        this.video.controls = true;
        this.video.playsInline = true;
        this.video.setAttribute('playsinline', '');
        this.video.preload = 'auto';
        if (data.poster) this.video.poster = data.poster;
        setVideoSource(this.video, data.src, data.srcFallback);
        this.video.addEventListener('ended', () => {
          if (!this.cta.hidden) this.cta.focus();
        });
        this.media.appendChild(this.video);
        this.src = data.src;
      }
      if (this.video.ended) this.video.currentTime = 0;

      document.documentElement.classList.add('mcv2-video-open');
      this.el.showModal();
      // Opened by a click, so the browser lets it play with sound; a refusal just leaves the controls.
      const play = this.video.play();
      if (play) play.catch(() => {});
      markVideoSeen();
    },
    close() {
      if (this.el && this.el.open) this.el.close();
    },

    /* "Try all White Rabbit": one of each sampler (ids from Liquid, which already checked stock and the
       per-order limit), then the drawer re-renders with them and the popup closes onto it. */
    addAll() {
      if (!this.ctaIds.length || this.cta.disabled) return;
      this.cta.disabled = true;
      this.cta.classList.add('loading');
      const body = JSON.stringify({
        items: this.ctaIds.map((id) => ({ id: parseInt(id, 10), quantity: 1 })),
        sections: sectionIds(),
        sections_url: window.location.pathname
      });
      fetch(`${theme.routes.cart_add_url}`, { ...fetchConfig('javascript'), body })
        .then((response) => response.json())
        .then((parsedState) => {
          if (parsedState.status === 422) {
            document.dispatchEvent(new CustomEvent('ajaxProduct:error', { detail: { errorMessage: parsedState.description } }));
            return;
          }
          this.close();
          renderCartSections(parsedState);
          document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: parsedState } }));
        })
        .catch((error) => console.error(error))
        .finally(() => {
          this.cta.disabled = false;
          this.cta.classList.remove('loading');
        });
    }
  };

  class Mcv2Video extends HTMLElement {
    connectedCallback() {
      this.bubble = this.querySelector('.mcv2-video__bubble');
      if (!this.bubble || this.bound) return;
      this.bound = true;
      this.classList.toggle('is-seen', isVideoSeen());

      this.bubble.addEventListener('click', (event) => {
        // Not a click on the shelf bar: keep the shelf as it is, and keep the drawer's outside-click from firing.
        event.stopPropagation();
        videoPeek.hide();
        videoModal.open(this);
      });

      if (!this.dataset.teaser) return;
      this.bubble.addEventListener('mouseenter', () => {
        if (!canHover.matches || reducedMotion.matches) return;
        this.hoverTimer = setTimeout(() => videoPeek.show(this), 120);
      });
      this.bubble.addEventListener('mouseleave', (event) => {
        clearTimeout(this.hoverTimer);
        if (videoPeek.contains(event.relatedTarget)) return;
        videoPeek.hide();
      });
    }

    disconnectedCallback() {
      clearTimeout(this.hoverTimer);
      videoPeek.hide();
    }
  }
  customElements.define('mcv2-video', Mcv2Video);

  // Sampler lines carry data-mcv2-max (per-order limit). Buttons already stop there via the input's max;
  // this clamps a typed value too. Capture phase, so it runs before <cart-items> sends the change.
  document.addEventListener('change', (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.hasAttribute('data-mcv2-max')) return;
    const max = parseInt(input.dataset.mcv2Max, 10);
    if (isNaN(max) || !(parseInt(input.value, 10) > max)) return;
    input.value = max;
    // Clamped back to the quantity already in the cart: nothing to send. Letting it through makes
    // cart.js report "You can only add N of this item" for an update that changed nothing.
    if (String(max) === input.getAttribute('value')) event.stopImmediatePropagation();
  }, true);

  // First load of the drawer contents (cart-drawer.js fires this after the lazy fetch): fade them in.
  // The class comes off again so later re-renders after cart changes don't replay the fade.
  document.addEventListener('cartdrawer:opened', () => {
    const drawer = document.getElementById('mini-cart');
    if (!drawer) return;
    drawer.classList.add('mcv2-fade-in');
    setTimeout(() => drawer.classList.remove('mcv2-fade-in'), 600);
  });

  // A click anywhere else in the drawer closes the open shelf. Bound once on document, because the
  // drawer markup is replaced on every cart change. Detached targets (clicks that re-rendered the
  // drawer) are ignored, since their position can no longer be told.
  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element) || !target.isConnected) return;
    const drawer = target.closest('#mini-cart');
    if (!drawer || target.closest('mcv2-shelf')) return;
    drawer.querySelectorAll('mcv2-shelf.is-open').forEach((shelf) => shelf.setOpen(false));
  });

})();
